/**
 * Standalone warehouse inventory management kiosk.
 *
 * Routed at `/kiosk/scanner/:token` OUTSIDE <ProtectedRoute>. No JWT, no
 * login, no idle timeout. The page runs always-on on a warehouse PC and
 * exposes ONLY the inventory management surface — receive / deduct /
 * count — for the one Storage bound to the token.
 *
 * There is NO navigation, NO access to other app pages, NO product
 * editing, NO settings. Its entire purpose is inventory management.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid-pro/locales';

import { createKioskApi } from '../services/kioskApi';
import InventoryActionPanel from './components/InventoryActionPanel';

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

const KioskInventoryPage = () => {
  const { token } = useParams();
  const [initState, setInitState] = useState({
    loading: true,
    error: null,
    storage: null,
    allowedModes: [],
    barcodeEnabled: false,
    userGroupName: '',
    businessProfile: {},
    tokenLabel: '',
  });
  const [clock, setClock] = useState(new Date());

  const apiClient = useMemo(() => createKioskApi(token), [token]);

  // Bootstrap: one-shot GET /kiosk/init/ to pull storage + permissions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/kiosk/init/');
        if (cancelled) return;
        setInitState({
          loading: false,
          error: null,
          storage: data.storage,
          allowedModes: data.allowed_modes || [],
          barcodeEnabled: !!data.barcode_enabled,
          userGroupName: data.user_group_name || '',
          businessProfile: data.business_profile || {},
          tokenLabel: data.token_label || '',
        });
      } catch (e) {
        if (cancelled) return;
        setInitState((s) => ({
          ...s,
          loading: false,
          error: e?.response?.data?.error || 'מסך לא פעיל — פנה למנהל',
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // Clock tick (banner shows the current time).
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000 * 30);
    return () => clearInterval(id);
  }, []);

  if (initState.loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (initState.error) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
            p: 4,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 500, fontSize: '1.1rem' }}>
            {initState.error}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const { storage, allowedModes, barcodeEnabled, businessProfile, tokenLabel } = initState;
  const clockText = clock.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  const dateText = clock.toLocaleDateString('he-IL');

  return (
    <ThemeProvider theme={theme}>
      <Box
        dir="rtl"
        sx={{
          minHeight: '100vh',
          bgcolor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Top banner ─────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: '#0f172a',
            color: '#fff',
            px: { xs: 2, sm: 4 },
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {businessProfile?.logo_url && (
              <Box
                component="img"
                src={businessProfile.logo_url}
                alt=""
                sx={{ maxHeight: 48, maxWidth: 60, objectFit: 'contain' }}
              />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {storage?.StorageName || 'מחסן'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {businessProfile?.business_name || ''}
                {tokenLabel ? ` · ${tokenLabel}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={clockText}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: '1rem' }}
            />
            <Chip
              label={dateText}
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}
            />
          </Box>
        </Box>

        {/* ── Main panel ─────────────────────────────────────── */}
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            py: { xs: 2, sm: 4 },
          }}
        >
          <InventoryActionPanel
            storageId={storage?.StorageID}
            storages={null}
            allowedModes={allowedModes}
            apiClient={apiClient}
            barcodeEnabled={barcodeEnabled}
            adjustEndpoint="/kiosk/inventory/adjust/"
            lookupEndpoint="/kiosk/products/lookup/"
            productAutocompleteEndpoint="/kiosk/products/lookup/"
            canPrintLabels={false}
            productLabel="מוצר"
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default KioskInventoryPage;
